import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Header from "@/components/Header";
import { toast } from "sonner";
import { Mail, Copy, CheckCircle2, Clock, AlertTriangle, Share2 } from "lucide-react";

/**
 * Simulated inbox of "claim my scouted profile" invites issued by this AD.
 * No real email is sent — invites are link-based; AD copies/shares the URL.
 */
export default function ClaimInbox() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/claim-invites/mine").then(({ data }) => {
      setInvites(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(load, []);

  const copy = (token) => {
    // Share via the OG-enabled URL so social previews unfurl with the personalized card.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/og/claim/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    toast.success("Shareable URL copied (with social preview)");
    setTimeout(() => setCopied(null), 2000);
  };

  const share = async (token, coachName) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/og/claim/${token}`;
    const text = `Hey ${coachName || "Coach"} — we built your CoachConnect AI profile from public data. Claim it (free, ~2 min) and you'll show up in matched job alerts.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Claim your CoachConnect AI profile", text, url });
      } catch (_) { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Outreach text copied to clipboard");
    }
  };

  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-5xl" data-testid="claim-inbox-page">
        <div className="text-xs tracking-widest text-volt">HEADHUNTER AGENT</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">CLAIM INVITES</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">
          Scouted coaches you&apos;ve invited to claim their profile. Until they accept, ADs can&apos;t see them as applicants.
          Email isn&apos;t wired yet — copy the share link (auto-unfurls on Twitter/LinkedIn/iMessage with a personalized preview).
        </p>

        <div className="mt-8 space-y-3" data-testid="claim-invites-list">
          {loading && <div className="text-neutral-500 text-sm">Loading…</div>}
          {!loading && invites.length === 0 && (
            <div className="border border-dashed border-neutral-800 p-8 text-center text-neutral-500 text-sm">
              No invites yet. Browse the <Link to="/ad/talent" className="text-volt underline">Talent Pool</Link> and use <em>Invite to claim</em> on an unclaimed profile.
            </div>
          )}
          {invites.map((inv) => (
            <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="border border-neutral-800 bg-appcard p-4 md:flex md:items-center md:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusBadge status={inv.coach_claimed ? "claimed" : inv.status} />
                  <span className="heading text-lg truncate">{inv.coach_name || "Unknown coach"}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                  <span>Sent {new Date(inv.created_at).toLocaleDateString()}</span>
                  {inv.coach_email && <span className="flex items-center gap-1"><Mail size={12} />{inv.coach_email}</span>}
                  {inv.expires_at && <span className="flex items-center gap-1"><Clock size={12} />expires {new Date(inv.expires_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="mt-3 md:mt-0 flex items-center gap-2 shrink-0 flex-wrap">
                <button
                  data-testid={`copy-claim-url-${inv.id}`}
                  onClick={() => copy(inv.token)}
                  className="min-h-[44px] px-4 border border-neutral-700 text-xs uppercase tracking-widest hover:border-volt hover:text-volt flex items-center gap-2"
                >
                  {copied === inv.token ? <CheckCircle2 size={14} className="text-volt" /> : <Copy size={14} />}
                  {copied === inv.token ? "Copied" : "Copy link"}
                </button>
                <button
                  data-testid={`share-claim-${inv.id}`}
                  onClick={() => share(inv.token, inv.coach_name)}
                  className="min-h-[44px] px-4 border border-neutral-700 text-xs uppercase tracking-widest hover:border-blaze hover:text-blaze flex items-center gap-2"
                >
                  <Share2 size={14} /> Share
                </button>
                <Link
                  to={`/claim/${inv.token}`}
                  data-testid={`preview-claim-${inv.id}`}
                  className="min-h-[44px] px-4 bg-blaze text-white heading text-sm hover:glow-blaze flex items-center"
                >
                  Preview
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "claimed") return <span className="px-2 py-0.5 text-[10px] tracking-widest bg-volt text-white flex items-center gap-1"><CheckCircle2 size={10} /> CLAIMED</span>;
  if (status === "expired") return <span className="px-2 py-0.5 text-[10px] tracking-widest bg-neutral-700 text-neutral-300 flex items-center gap-1"><AlertTriangle size={10} /> EXPIRED</span>;
  return <span className="px-2 py-0.5 text-[10px] tracking-widest bg-blaze/20 text-blaze border border-blaze/40 flex items-center gap-1"><Clock size={10} /> PENDING</span>;
}
